// hooks/usePredefinedPrompts.ts
import { useMemo } from "react";
import { ANALYTICS_CHART_TYPE } from "@resources/Constants";
import { useLang } from "@hooks/useLang";

export type PromptItem = {
  title: string;
  desc: string;
};

export const usePredefinedPrompts = () => {
  const { translate } = useLang();

  const COMMON_TITLES = {
    VISITOR_TREND_ANALYSIS: translate(
      "analytics_insights.visitor_trend_analysis"
    ),
    OCCUPANCY_TREND_ANALYSIS: translate(
      "analytics_insights.occupancy_trend_analysis"
    ),
    TREND_ANALYSIS: translate("analytics_insights.trend_analysis"),
    PEAK_TRAFFIC_ANALYSIS: translate(
      "analytics_insights.peak_traffic_analysis"
    ),
    TREND_PREDICTION: translate("analytics_insights.trend_prediction"),
    INCIDENT_PATTERNS_ANALYSIS: translate(
      "analytics_insights.incident_patterns_analysis"
    ),
    CRITICAL_LOCATION_IDENTIFICATION: translate(
      "analytics_insights.critical_location_identification"
    ),
    INCIDENT_PREDICTION: translate("analytics_insights.incident_prediction"),
    CROWD_TREND_ANALYSIS: translate("analytics_insights.crowd_trend_analysis"),
    VEHICLE_QUEUES_TREND_ANALYSIS: translate(
      "analytics_insights.vehicle_queues_trend_analysis"
    )
  };

  const sharedPrompts = {
    visitorTrend: {
      title: COMMON_TITLES.VISITOR_TREND_ANALYSIS,
      desc: translate("analytics_insights.visitor_trend_analysis_desc")
    },
    occupancyTrend: {
      title: COMMON_TITLES.VISITOR_TREND_ANALYSIS,
      desc: translate("analytics_insights.occupancy_trend_analysis_desc")
    },
    trendAnalysis: {
      title: COMMON_TITLES.TREND_ANALYSIS,
      desc: translate("analytics_insights.trend_analysis_desc")
    },
    peakTraffic: {
      title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
      desc: translate("analytics_insights.peak_traffic_analysis_desc")
    },
    trendPrediction: {
      title: COMMON_TITLES.TREND_PREDICTION,
      desc: translate("analytics_insights.trend_prediction_desc")
    }
  };

  const promptMap = {
    [ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT]: [
      sharedPrompts.visitorTrend,
      sharedPrompts.peakTraffic,
      sharedPrompts.trendPrediction
    ],
    [ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY]: [
      sharedPrompts.occupancyTrend,
      sharedPrompts.peakTraffic,
      sharedPrompts.trendPrediction
    ],
    [ANALYTICS_CHART_TYPE.PEOPLE_QUEUES]: [
      {
        title: translate("analytics_insights.people_queues_trend_analysis"),
        desc: translate("analytics_insights.people_queues_trend_analysis_desc")
      },
      {
        title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
        desc: translate(
          "analytics_insights.people_queues_peak_traffic_analysis_desc"
        )
      },
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate(
          "analytics_insights.people_queues_trend_prediction_desc"
        )
      }
    ],
    [ANALYTICS_CHART_TYPE.SLIP_AND_FALL]: [
      {
        title: COMMON_TITLES.INCIDENT_PATTERNS_ANALYSIS,
        desc: translate("analytics_insights.incident_patterns_analysis_desc")
      },
      {
        title: COMMON_TITLES.CRITICAL_LOCATION_IDENTIFICATION,
        desc: translate(
          "analytics_insights.critical_location_identification_desc"
        )
      },
      {
        title: COMMON_TITLES.INCIDENT_PREDICTION,
        desc: translate("analytics_insights.incident_prediction_desc")
      }
    ],
    [ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC]: [
      {
        title: COMMON_TITLES.VISITOR_TREND_ANALYSIS,
        desc: translate(
          "analytics_insights.visitor_area_traffic_trend_analysis_desc"
        )
      },
      {
        title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
        desc: translate(
          "analytics_insights.visitor_area_traffic_peak_traffic_analysis_desc"
        )
      },
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate(
          "analytics_insights.visitor_area_traffic_trend_prediction_desc"
        )
      }
    ],
    [ANALYTICS_CHART_TYPE.CROWD_COUNTING]: [
      {
        title: COMMON_TITLES.CROWD_TREND_ANALYSIS,
        desc: translate("analytics_insights.crowd_trend_analysis_desc")
      },
      {
        title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
        desc: translate("analytics_insights.peak_traffic_analysis_desc")
      },
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate("analytics_insights.crowd_trend_prediction_desc")
      }
    ],
    [ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT]: [
      {
        title: COMMON_TITLES.TREND_ANALYSIS,
        desc: translate("analytics_insights.shopping_cart_trend_analysis_desc")
      },
      {
        title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
        desc: translate("analytics_insights.peak_traffic_analysis_desc")
      },
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate(
          "analytics_insights.shopping_cart_trend_prediction_desc"
        )
      }
    ],
    [ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE]: [
      {
        title: COMMON_TITLES.TREND_ANALYSIS,
        desc: translate(
          "analytics_insights.shopping_cart_in_use_trend_analysis_desc"
        )
      },
      {
        title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
        desc: translate("analytics_insights.peak_traffic_analysis_desc")
      },
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate(
          "analytics_insights.shopping_cart_trend_prediction_desc"
        )
      }
    ],
    [ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES]: [
      {
        title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
        desc: translate(
          "analytics_insights.shopping_cart_queues_peak_traffic_analysis_desc"
        )
      },
      {
        title: translate(
          "analytics_insights.shopping_cart_queues_trend_analysis"
        ),
        desc: translate(
          "analytics_insights.shopping_cart_queues_trend_analysis_desc"
        )
      },
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate(
          "analytics_insights.shopping_cart_queues_trend_prediction_desc"
        )
      }
    ],
    [ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT]: [
      {
        title: COMMON_TITLES.TREND_ANALYSIS,
        desc: translate(
          "analytics_insights.cumulative_vehicle_trend_analysis_desc"
        )
      },
      {
        title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
        desc: translate("analytics_insights.peak_traffic_analysis_desc")
      },
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate(
          "analytics_insights.cumulative_vehicle_trend_prediction_desc"
        )
      }
    ],
    [ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY]: [
      {
        title: COMMON_TITLES.TREND_ANALYSIS,
        desc: translate(
          "analytics_insights.vehicle_occupancy_trend_analysis_desc"
        )
      },
      {
        title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
        desc: translate("analytics_insights.peak_traffic_analysis_desc")
      },
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate(
          "analytics_insights.cumulative_vehicle_trend_prediction_desc"
        )
      }
    ],
    [ANALYTICS_CHART_TYPE.VEHICLE_QUEUES]: [
      {
        title: COMMON_TITLES.VEHICLE_QUEUES_TREND_ANALYSIS,
        desc: translate("analytics_insights.vehicle_queues_trend_analysis_desc")
      },
      {
        title: COMMON_TITLES.PEAK_TRAFFIC_ANALYSIS,
        desc: translate(
          "analytics_insights.vehicle_queues_peak_traffic_analysis_desc"
        )
      },
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate(
          "analytics_insights.vehicle_queues_trend_prediction_desc"
        )
      }
    ],
    [ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS]: [
      {
        title: translate("analytics_insights.stopped_vehicle_trend_analysis"),
        desc: translate(
          "analytics_insights.stopped_vehicle_trend_analysis_desc"
        )
      },
      sharedPrompts.peakTraffic,
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate(
          "analytics_insights.stopped_vehicle_trend_prediction_desc"
        )
      }
    ],
    [ANALYTICS_CHART_TYPE.SLOW_TRAFFIC]: [
      {
        title: translate("analytics_insights.slow_traffic_trend_analysis"),
        desc: translate("analytics_insights.slow_traffic_trend_analysis_desc")
      },
      sharedPrompts.peakTraffic,
      {
        title: COMMON_TITLES.TREND_PREDICTION,
        desc: translate("analytics_insights.slow_traffic_trend_prediction_desc")
      }
    ]
  } satisfies Record<string, PromptItem[]>;

  return useMemo(() => promptMap, [translate]);
};
